/** 아바타 미설정 시 카카오톡 스타일 기본 아이콘 (회색 배경 + 흰색 사람 실루엣) */
export const DEFAULT_AVATAR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='32' fill='%23C5CCD3'/%3E%3Ccircle cx='32' cy='24' r='10' fill='%23fff'/%3E%3Cellipse cx='32' cy='52' rx='18' ry='14' fill='%23fff'/%3E%3C/svg%3E";
/**
 * 아바타 <img onError> 공용 핸들러 — 서버 파일이 지워졌거나 URL이 깨졌을 때 브라우저의
 * "깨진 이미지 + alt 텍스트" 대신 기본 아바타로 조용히 대체한다. 무한 루프 방지를 위해
 * 이미 기본 아바타면 건드리지 않는다.
 */
export const handleAvatarError = (e: { currentTarget: HTMLImageElement }): void => {
  const el = e.currentTarget;
  if (el.src !== DEFAULT_AVATAR) el.src = DEFAULT_AVATAR;
};

export interface Affiliate {
  id: string;
  label: string;
}

/**
 * 표시용 접속 상태. 서버(realtime.computePresenceStatus)가 계정 단위로 판정한 값이고,
 * away는 유휴 5분·화면 잠금으로 자동 전환되며 busy는 사용자가 직접 고른 경우만이다.
 * 한글 라벨과 점 색상은 src/utils/presence.ts가 소유한다.
 */
export type PresenceStatus = 'online' | 'offline' | 'away' | 'busy';

/** 사용자가 직접 고를 수 있는 상태. null = 자동 판정(접속중). 'online'이 없는 이유는
 *  강제 온라인을 만들지 않기 때문이다(서버 MANUAL_STATUS_VALUES 주석 참고). */
export type PresenceManual = 'away' | 'busy' | null;

export interface User {
  id: number | string; // 사번이 문자열일 수도 있으므로 유연하게 변경
  userid?: string;
  username: string;
  avatar: string;
  status: PresenceStatus;
  depart?: string; // 부서 정보 추가
  affiliateId?: string; // 계열사 ID
  isAdmin?: boolean; // 시스템 관리자 여부
  /**
   * 전역 역할(system_admins.role). 로그인 응답으로만 채워지며 UI 노출 판정에만 쓴다
   * (관리자 콘솔 감사 탭 표시, 감사 조회 통지 배너 마운트). 최종 권한은 언제나 서버가 판정한다.
   * `undefined` = 이 필드가 없던 시절의 저장 세션(재로그인 전) — 노출 필터를 적용하지 않는다.
   */
  role?: 'admin' | 'auditor' | 'super_admin' | null;
  /**
   * OS 알림(데스크톱 토스트·웹 시스템 알림·모바일 웹푸시)에 표시할 내용 수준.
   * 기기별이 아니라 계정 전체 설정이며 서버 user_profiles가 권위다.
   * `undefined` = 모름(구 서버 응답·프로필 조회 실패) — 기존 저장값을 유지하고,
   * 그래도 없으면 표시 단계에서 'full'로 접는다.
   */
  notificationPrivacy?: NotificationPrivacy;
}

/**
 * 상대방 프로필(GET /api/users/:userId/profile) — 프로필 모달 전용.
 * email은 인트라넷 userEmail(@ 앞부분) + 계열사 메일 도메인을 서버가 조립한 값이고 없으면 null.
 * avatar는 user_profiles 값이라 null이면 "서버에 저장된 사진 없음"이다.
 */
export interface UserProfileInfo {
  id: string;
  name: string;
  depart: string | null;
  email: string | null;
  affiliateId: string;
  affiliateLabel: string;
  avatar: string | null;
  /** 원격지원 담당자(명단 또는 super_admin)인가 — 직원에게 '원격지원 요청' 버튼을 보인다. 구버전 서버는 없다 */
  remoteSupporter?: boolean;
}

/**
 * 알림에 표시할 내용 수준.
 *  full   — 보낸 사람 + 메시지 내용
 *  sender — 보낸 사람만 (내용 숨김)
 *  hidden — 아무것도 표시하지 않음
 */
export type NotificationPrivacy = 'full' | 'sender' | 'hidden';

export interface Department {
  name: string;
  users: User[];
  isOpen?: boolean; // UI 상태용
}

export interface Attachment {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string; // base64 data URL 또는 서버 URL
  thumbnail?: string; // 이미지 썸네일
  /**
   * 보관 기간 만료 시각(ISO). NULL/undefined면 무기한 — 기능 도입 이전 첨부가 전부 여기 해당한다.
   * 서버가 저장 시점에 계산해 넣고, 만료 판정은 src/utils/attachmentExpiry.ts 하나만 한다.
   */
  expiresAt?: string | null;
}

/**
 * 링크 미리보기 카드 (OG 메타).
 * 서버는 수집이 끝난 것(status='ok')만 내려보낸다 — 수집 중·실패는 필드가 null이라
 * 클라이언트에 상태 분기가 없다(카드를 그리지 않는다).
 */
export interface LinkPreview {
  /** 클릭 대상. 서버가 정규화한 값(fragment 제거) */
  url: string;
  /** 표시용 도메인 */
  host: string;
  title?: string | null;
  description?: string | null;
  siteName?: string | null;
  /**
   * 썸네일. 서버가 내려받아 사내에 저장한 **루트 상대경로**(`/uploads/link-previews/...`).
   * ⚠ <img src>에 넣기 전 반드시 resolveServerUrl()을 통과시킬 것 —
   *   Tauri는 화면 origin이 tauri.localhost라 상대경로를 그대로 꽂으면 404가 난다.
   */
  imagePath?: string | null;
  imageWidth?: number | null;
  imageHeight?: number | null;
}

/** 서버가 저장·반환하는 멘션 구조 (message_mentions 평문 인덱스) */
export interface MessageMention {
  type: 'user' | 'dept' | 'here' | 'all'; // 'here'는 폐지된 레거시 (신규 생성 없음, 과거 행 렌더용)
  targetId?: string | null; // user: userId / dept: 부서명 / here·all: null
  name: string;             // 렌더 대조용 표시명 ('홍길동'·'개발팀'·'here'·'all')
}

/** 전송 시 클라이언트가 서버로 보내는 멘션 입력 (서버가 활성 멤버로 클램프) */
export type MentionInput =
  | { type: 'user'; userId: string; name: string }
  | { type: 'dept'; name: string; userIds: string[] }
  | { type: 'all' }; // 대화방 참여자 전원 — 관리자·방장만

/**
 * 예약 메시지에 담긴 첨부 — 예약 시점에 이미 올린 파일(`/uploads/{날짜}/{uuid}_원본`).
 * attachments 행은 발송 시점에 생기므로 id·expiresAt이 없다(보관 기간은 보낸 날부터 센다).
 * ⚠ url은 서버 루트 상대경로 — <img src>에는 resolveServerUrl()을 통과시킬 것(Tauri origin).
 */
export interface ScheduledAttachment {
  name: string;
  url: string;
  size: number;
  type: string;
}

/**
 * 예약 메시지 (지정 시각 자동 발송, 서버 scheduled_messages 행).
 * 상태는 타임스탬프 3종 — sentAt/canceledAt/failedAt 셋 다 null이면 대기 중.
 * id는 PG BIGINT라 문자열이다 (비교는 String() 정규화 규약).
 */
export interface ScheduledMessage {
  id: string;
  channelId: number;
  content: string;           // 평문 (본인 것만 내려온다). 첨부만 있으면 ATTACHMENT_ONLY_TEXT
  mentions: MentionInput[];
  /** 구서버 응답에는 없을 수 있다 — 읽는 쪽은 `?? []` */
  attachments?: ScheduledAttachment[];
  scheduledAt: string;       // ISO — 서버가 초를 내림해 저장 (분 단위)
  sentAt: string | null;
  canceledAt: string | null;
  failedAt: string | null;
  failReason: string | null; // 실패 사유 (사용자 노출 문구 그대로)
  isAdminNotice: boolean;    // 관리자 콘솔 공지 예약 — 일반 목록에서는 걸러진다
  messageId: string | null;  // 발송 성공 시 생성된 메시지 id
  createdAt: string;
}

export interface Message {
  id: number;
  channelId: number;
  text: string;
  senderId: number | string;
  senderName: string;
  senderAvatar?: string;
  timestamp: string;
  msgType?: 'text' | 'image' | 'file' | 'system';
  replyToId?: number | null;
  deletedAt?: string | null;
  updatedAt?: string | null;
  attachments?: Attachment[];
  mentions?: MessageMention[];
  /** 전달(Forward)된 메시지의 원 발신자 라벨 — 출처 채널은 미노출 (가시성 누출 방지) */
  forwardedFrom?: { senderName: string } | null;
  reactions?: Reaction[];
  unreadCount?: number;
  /** 본문 첫 링크의 미리보기 카드. 수집 전·실패·링크 없음이면 null */
  linkPreview?: LinkPreview | null;
  /** 대화방 일정 공유 카드. 일반 메시지는 null(또는 없음 — 구버전 서버) */
  calendarEvent?: CalendarCard | null;
}

// ============================================
// 일정 캘린더 (server/routes/calendar.js)
// ============================================

export type CalendarScope = 'personal' | 'dept' | 'company';
export type CalendarRepeatFreq = 'daily' | 'weekly' | 'monthly' | 'yearly';
export type CalendarResponse = 'pending' | 'accepted' | 'declined';

/** 범위 조회의 회차 하나 — 반복 일정은 서버가 펼쳐 보낸다(클라이언트에 전개 규칙 사본 없음) */
export interface CalendarOccurrence {
  /** `${eventId}:${startDate}` — 렌더 키 */
  key: string;
  /** 숫자 문자열, 인트라넷 부서별일정은 'intra:N' */
  eventId: string;
  source: 'chat' | 'intranet';
  scope: CalendarScope;
  title: string;
  location: string;
  allDay: boolean;
  /** ISO. 끝은 배타 */
  start: string;
  end: string;
  /** KST 날짜 키 — 시작일 / 마지막 날(포함) */
  startDate: string;
  endDate: string;
  repeat: CalendarRepeatFreq | null;
  ownerName: string;
  myRole: 'owner' | 'attendee' | 'dept' | 'company' | 'shared' | null;
  myResponse: CalendarResponse | null;
  canEdit: boolean;
  remind: boolean;
}

export interface CalendarCompanyTarget {
  /** null = 전체 계열사 공통 */
  id: string | null;
  label: string;
}

export interface CalendarRangeResult {
  occurrences: CalendarOccurrence[];
  /** 날짜 키 → 공휴일 이름 */
  holidays: Record<string, string>;
  deptName: string | null;
  canCreate: { dept: boolean; company: boolean; companyAffiliates: CalendarCompanyTarget[] };
  intranet: 'ok' | 'unavailable' | 'none';
  deptUnavailable: boolean;
  truncated: boolean;
}

export interface CalendarAttendee {
  userId: string;
  name: string;
  response: CalendarResponse;
}

export interface CalendarEventDetail {
  id: string;
  source: 'chat' | 'intranet';
  scope: CalendarScope;
  affiliateId?: string | null;
  deptName?: string | null;
  title: string;
  location: string;
  description: string;
  allDay: boolean;
  /** 첫 회차 */
  start: string;
  end: string;
  startDate: string;
  endDate: string;
  repeat: { freq: CalendarRepeatFreq; until: string | null } | null;
  repeatSummary: string;
  exdates: string[];
  remindOffset: number | null;
  ownerId: string | null;
  ownerName: string;
  attendees: CalendarAttendee[];
  myRole: CalendarOccurrence['myRole'];
  myResponse: CalendarResponse | null;
  canEdit: boolean;
  canDelete: boolean;
  canRespond: boolean;
  canShare: boolean;
  /** 공유 메시지로 연 읽기 전용 열람 */
  viaMessage?: boolean;
  updatedAt?: string;
  /** 인트라넷 부서별일정 화면 주소 */
  intranetUrl?: string;
}

/** 대화방 일정 공유 카드 — 일정의 현재 값(공유 시점 사본이 아니다) */
export interface CalendarCard {
  id: string;
  deleted?: boolean;
  /** 카드 조회가 일시적으로 실패 */
  unavailable?: boolean;
  scope?: CalendarScope;
  title?: string;
  location?: string;
  allDay?: boolean;
  repeat?: CalendarRepeatFreq | null;
  repeatSummary?: string;
  ownerName?: string;
  /** 반복이 끝났거나 지난 회차뿐 */
  ended?: boolean;
  start?: string;
  end?: string;
  /** 카드가 보여 주는 회차의 날짜 키 */
  date?: string;
  /** 서버가 만든 표시 문구 */
  when?: string;
}

/** 알림함 행의 앱 안 이동 참조 */
export interface ActivityRef {
  kind: 'calendar';
  eventId: string;
  date: string;
}

/**
 * 날짜 점프 조회 결과
 * anchorId가 null이면 선택한 날짜 이후 대화가 없다는 뜻
 */
export interface JumpResult {
  anchorId: number | null;
  messages: Message[];
  hasMoreOlder: boolean;
  hasMoreNewer: boolean;
}

export interface MessageReadPayload {
  channelId: number;
  userId: string;
  messageIds: number[];
  unreadCounts: Record<number, number>;
}

export type ChannelType = 'public' | 'private' | 'group' | 'dm' | 'self';

export interface Channel {
  id: number;
  name: string | null;
  type: ChannelType;
  description?: string;
  createdBy?: string;
  createdAt?: string;
  isArchived?: boolean;
  affiliateId?: string | null;
  // 사용자별 정보
  role?: 'owner' | 'admin' | 'member';
  lastReadAt?: string | null;
  isMuted?: boolean;
  isPinned?: boolean;
  pinnedAt?: string | null;
  // 집계 정보
  memberCount?: number;
  unreadCount?: number;
  /**
   * 미리보기(lastMessage)가 가리키는 메시지의 id. 클라이언트가 "이 갱신이 더 최신인가"를
   * 판정하는 유일한 근거다 — 없으면 나중에 도착한 이벤트가 무조건 이겨서 미리보기가
   * 이전 메시지로 되돌아간다(App.tsx applyLastMessage 참조). PG BIGINT라 문자열.
   */
  lastMessageId?: string | null;
  lastMessage?: string | null;
  lastMessageAt?: string | null;
  /**
   * 그룹방 이름을 직접 정하지 않았다(서버 channels.name_auto). 이때 `name`은 서버가 보는 사람마다 계산한
   * '나를 뺀 현재 멤버' 이름이라 사람마다 다르고 멤버가 들고 나면 바뀐다(channel:info-updated).
   * 이름 변경 창은 빈 칸으로 열린다.
   */
  nameAuto?: boolean;
  // DM 전용 — 상대방 사번/아바타 (서버 getChannelListForUser가 채움)
  dmPartnerId?: string | null;
  dmPartnerAvatar?: string | null;
  // 채널 공지 (상단 고정 배너). null/undefined = 공지 없음
  notice?: ChannelNotice | null;
  /**
   * 초안 DM 전용 — 첫 메시지·통화 시점에 POST /channels/dm에 넘길 상대 정보.
   * id === DRAFT_CHANNEL_ID일 때만 존재하며, 이 객체는 절대 `channels` 배열이나
   * localStorage에 들어가서는 안 된다(목록·검색·부팅 복원이 유령 방을 되살린다).
   */
  draftPartner?: { id: string; name: string };
}

/**
 * 아직 서버에 존재하지 않는 '초안 DM'의 채널 id.
 *
 * 온라인 탭·조직도·검색·멤버 목록에서 사용자를 클릭해도 방을 만들지 않고 이 id로 채팅창만 열며,
 * 첫 메시지 전송 또는 통화 시작 시점에 POST /api/channels/dm으로 실제 방을 만든다
 * (예전에는 클릭 즉시 방이 생겨 아무 말도 안 걸었는데 상대 목록에 빈 방이 쌓였다).
 *
 * 0을 쓰는 이유: PG BIGSERIAL은 1부터라 실제 id와 절대 겹치지 않고, activeChannelIdRef를
 * 읽는 12곳의 기존 `if (channelId)` truthy 가드가 그대로 초안 가드가 된다.
 */
export const DRAFT_CHANNEL_ID = 0;

export const isDraftChannel = (c?: Channel | null): boolean =>
  !!c && c.id === DRAFT_CHANNEL_ID;

/**
 * 대화방 이름 최대 길이. 생성(CreateGroupModal)·이름 변경(RenameChannelModal) 입력의
 * maxLength이자 서버 `PUT /api/channels/:id`의 검증 상한(server/index.js)과 같은 값이어야 한다.
 * DB 컬럼은 VARCHAR(100)이지만 UI 상한을 서버가 강제한다.
 */
export const CHANNEL_NAME_MAX_LEN = 50;

/**
 * 대화방 검색용 참여자 인덱스 — { channelId: [멤버 이름...] } (나 자신은 제외).
 * 서버 GET /api/channels/member-index가 dm/group/private 채널만 담아 준다
 * (공개 채널은 전원이 멤버라 어떤 이름을 쳐도 다 걸려서 제외).
 */
export type ChannelMemberIndex = Record<string, string[]>;

export interface ChannelNotice {
  messageId: number;
  text: string;
  senderName: string;
  setBy?: string;
  setByName?: string;
  setAt?: string | null;
}

/**
 * 채널 미리보기 정정 — 마지막 메시지가 삭제됐을 때만 서버가 보낸다.
 * 이 이벤트만 단조성 가드를 우회한다 (삭제는 lastMessageId가 내려가는 유일한 경우).
 * lastMessageId가 null이면 그 채널에 남은 메시지가 없다는 뜻.
 */
export interface ChannelLastMessagePayload {
  channelId: number;
  lastMessageId: string | null;
  lastMessage: string | null;
  lastMessageAt: string | null;
}

export interface ChannelNoticeChangedPayload {
  channelId: number;
  notice: ChannelNotice | null;
}

/** 채널 첨부 서랍 항목 (GET /channels/:id/attachments) */
export interface ChannelAttachmentItem {
  id: number;
  name: string;
  size: number | null;
  type: string | null; // mime_type
  url: string;
  expiresAt?: string | null; // 보관 기간 만료 시각 (NULL = 무기한)
  messageId: number;
  senderName: string;
  createdAt: string;
}

export interface ChannelAttachmentsResult {
  items: ChannelAttachmentItem[];
  hasMore: boolean;
}

export interface ChannelMember {
  userId: string;
  userName: string;
  avatar?: string | null;
  role: 'owner' | 'admin' | 'member';
  joinedAt: string;
  lastReadAt?: string | null;
  isMuted?: boolean;
  // 온라인 상태 (런타임)
  status?: PresenceStatus;
}

export interface LoginResponse {
  success: boolean;
  message?: string;
  token?: string;
  user?: User;
  /**
   * 실패 사유 코드. 'INVALID_CREDENTIALS'(401, 아이디·비밀번호 불일치) / 'NOT_APPROVED'(403, 로그인 승인
   * 명단에 없음) / 'RATE_LIMITED'(429, 시도 제한). 400·5xx와 네트워크 오류에는 없다.
   * 자동 로그인은 'INVALID_CREDENTIALS'일 때만 저장된 비밀번호를 지운다(Login.tsx).
   *
   * 기기 신뢰(server/services/deviceTrust.js) — 비밀번호는 맞았으므로 저장값을 지우지 않는다:
   * 'DEVICE_PENDING'(403, 승인 대기 — device에 확인 코드) / 'DEVICE_NOT_REGISTERED'(403, 미등록 — canRequest) /
   * 'DEVICE_BLOCKED'(403, 거절·해제) / 'DEVICE_REQUIRED'(403, 기기 키 없음) / 'APP_UPDATE_REQUIRED'(403, 키 없는
   * 구버전 PC 앱) / 'DEVICE_PROOF_INVALID'(400) / 'DEVICE_PENDING_FULL'(503)
   */
  code?: string;
  /**
   * 승인 웹 기기의 비밀번호 로그인에만 실리는 재개 비밀값 — 토큰이 만료된 뒤 기기 키로 세션을 다시 열 때 함께
   * 보낸다(POST /api/auth/device-resume). 직접 로그아웃하면 서버·로컬 양쪽에서 지운다(보안 감사 2026-11 C9)
   */
  resumeSecret?: string;
  /** 이 기기 상태(성공 시 observe 단계 안내용, DEVICE_PENDING이면 확인 코드 포함) */
  device?: import('./services/deviceIdentity').DeviceStatusInfo;
  /** DEVICE_NOT_REGISTERED일 때 이 기기에서 승인 요청을 보낼 수 있는가(웹 키) */
  canRequest?: boolean;
  /** DEVICE_BLOCKED일 때 거절·해제 사유 */
  reason?: string;
}

// ============================================
// Socket 이벤트 타입
// ============================================

// 사용자 상태
export interface UserStatusPayload {
  userId: string;
  userName: string;
  status: PresenceStatus;
  depart?: string;
  affiliateId?: string;
  avatar?: string | null;
}

export interface OnlineUser {
  userId: string;
  userName: string;
  status: PresenceStatus;
  lastSeen?: string;
  depart?: string;
  affiliateId?: string;
  avatar?: string | null;
}

// 채널 이벤트
export interface ChannelJoinPayload {
  channelId: number;
  userId: string;
  userName: string;
}

export interface ChannelReadPayload {
  channelId: number;
  userId: string;
  readAt: string;
}

// 메시지 이벤트
export interface MessagePayload {
  id: number;
  channelId: number;
  senderId: string;
  senderName: string;
  text: string;
  msgType: 'text' | 'image' | 'file' | 'system';
  replyToId?: number | null;
  timestamp: string;
  mentions?: MessageMention[];
  /** 이미 수집이 끝난 URL만 실린다(재공유 즉시 표시). 처음 보는 링크는 message:link-preview로 뒤따라온다 */
  linkPreview?: LinkPreview | null;
  /** 일정 공유 카드 */
  calendarEvent?: CalendarCard | null;
  // 채널 정보 (채널 목록에 없을 때 추가용)
  channel?: {
    id: number;
    name: string | null;
    type: ChannelType;
    description?: string;
  } | null;
}

// 메시지 알림 이벤트 (채널 정보 포함)
export interface MessageNotifyPayload extends MessagePayload {
  channel: {
    id: number;
    name: string | null;
    type: ChannelType;
    description?: string;
  };
  /** 이 수신자가 멘션 대상이면 true — 뮤트 채널의 토스트/OS 알림 억제를 우회한다 */
  mentionsMe?: boolean;
}

// 로그인 시 보류 알림 배치 이벤트 (채널별 개별 emit 대신 1회 수신)
export interface PendingNotification {
  channelId: number;
  channel: {
    id: number;
    name: string | null;
    type: ChannelType;
    description?: string;
    isArchived?: boolean;
    affiliateId?: number | null;
  };
  unreadCount: number;
  isPendingNotification: true;
}

export interface MessageNotifyBatchPayload {
  notifications: PendingNotification[];
}

export interface MessageDeletedPayload {
  messageId: number | string;
  channelId: number;
  deletedBy: string;
  deletedAt: string;
}

export interface MessageEditedPayload {
  messageId: number;
  channelId: number;
  content: string;
  updatedAt: string;
  editedBy: string;
  /**
   * 편집 후 미리보기. 링크를 지웠으면 null이 실려 카드가 사라진다.
   * 필드 자체가 없으면(구버전 서버) '모름'이므로 기존 값을 유지해야 한다.
   */
  linkPreview?: LinkPreview | null;
}

/** 워커가 수집을 마쳤을 때 (server/services/linkPreview.js) */
export interface MessageLinkPreviewPayload {
  channelId: number;
  messageId: number | string;
  preview: LinkPreview | null;
}

/**
 * 공유한 일정이 삭제됐다 (server/services/calendarCards.js propagateDeleted).
 * 방 단위 1건 — 그 방에서 calendarEvent.id가 같은 메시지를 전부 고친다. 제목은 싣지 않는다(클라가 가진 값을 유지)
 */
export interface MessageCalendarCardPayload {
  channelId: number | string;
  eventId: string;
  card: CalendarCard;
}

// 리액션 관련 타입
export interface Reaction {
  emoji: string;
  count: number;
  users: Array<{ userId: string; userName: string }>;
}

export interface ReactionUpdatedPayload {
  messageId: number;
  channelId: number;
  reactions: Reaction[];
}

// 타이핑 이벤트
export interface TypingPayload {
  channelId: number;
  userId: string;
  userName?: string;
}

// 멤버 이벤트
export interface MemberAddedPayload {
  channelId: number;
  members: Array<{ id: string; name: string }>;
  invitedBy: string;
}

export interface MemberRemovedPayload {
  channelId: number;
  userId: string;
  leftAt: string;
}

// ============================================
// 감사(Audit) 관련 타입
// ============================================

export type GlobalRole = 'user' | 'admin' | 'auditor' | 'super_admin';

export interface UserRole {
  userId: string;
  userName: string;
  role: GlobalRole;
  grantedBy: string;
  createdAt: string;
}

export interface AuditLog {
  id: number;
  auditorId: string;
  auditorName: string;
  auditorRole: string;
  actionType: string;
  targetType: string | null;
  targetId: string | null;
  targetName: string | null;
  reason: string;
  createdAt: string;
  metadata: Record<string, any>;
}

export interface AuditNotification {
  id: number;
  auditLogId: number;
  channelId?: number;
  message: string;
  actionType: string;
  auditorName: string;
  readAt?: string;
  createdAt: string;
}

// ============================================
// 통합 알림함 (Activity — 멘션/답글/리액션)
// ============================================
export interface ActivityItem {
  id: number;
  /** 'system' = 외부 시스템 알림(전자결재 등) — 채널·메시지가 없고 클릭하면 link를 연다 */
  type: 'mention' | 'reply' | 'reaction' | 'system';
  /** system 행은 null */
  channelId: number | null;
  /** DM은 null — actorName으로 폴백해 표시 */
  channelName: string | null;
  /** system 행은 null */
  messageId: number | null;
  actorId: string;
  /** system 행은 출처 라벨('전자결재') */
  actorName: string;
  /** 대화 알림은 메시지 미리보기, system은 문서 제목 */
  preview: string;
  emoji?: string | null;
  /** 알림함 탭 키 — null(또는 없음) = 대화 알림, 그 외 = 출처(server/config/integrations.js) */
  source?: string | null;
  /** system 전용: 알림 제목 */
  title?: string;
  /** system 전용: 클릭 시 여는 URL. 화면에는 원점까지만 표시한다 */
  link?: string | null;
  /** system 전용: 앱 안 이동 참조 — 일정 알림은 캘린더의 그 날짜로 간다 */
  ref?: ActivityRef | null;
  readAt?: string | null;
  createdAt: string;
}

/** 알림함 탭 — 서버 GET /api/activity/unread-count의 tabs. 시스템 출처가 없으면 빈 배열 */
export interface ActivityTab {
  key: string;
  label: string;
  unread: number;
}

export interface AuditMessage {
  id: number;
  channelId: number;
  channelName?: string;
  channelType?: string;
  senderId: string;
  senderName: string;
  content: string;
  msgType: string;
  deletedAt: string | null;
  createdAt: string;
  attachment?: {
    fileName: string;
    filePath: string;
    mimeType: string;
  } | null;
}

// ============================================
// 화상 통화 관련 타입
// ============================================

export type CallStatus = 'idle' | 'calling' | 'incoming' | 'connected' | 'ended';

/** 그 참가자와의 미디어 연결 — connecting(잇는 중) / connected / reconnecting(끊겨서 다시 잇는 중) / failed(다시 잇지 못함) */
export type CallConnState = 'connecting' | 'connected' | 'reconnecting' | 'failed';

export interface CallParticipant {
  userId: string;
  userName: string;
  avatar?: string;
  stream?: MediaStream;
  isMuted?: boolean;
  isCameraOff?: boolean;
  isSharingScreen?: boolean;
  /** 서버가 매긴 참가 순번 — 두 사람 사이에서 순번이 작은 쪽(먼저 들어온 쪽)이 offer를 보낸다 */
  seq?: number;
  connState?: CallConnState;
}

export interface CallRecorder {
  userId: string;
  userName: string;
}

export interface ActiveCall {
  channelId: number;
  channelName: string;
  callId: string;        // 통화 세션 고유 ID
  initiatorId: string;
  participants: CallParticipant[];
  startedAt: Date;
  status: CallStatus;
  /** 'dm'이면 상대가 나가면 끝난다. 'group'은 마지막 사람이 나갈 때까지 */
  channelType?: string;
  /** 참가 시점에 이미 녹화 중이던 사람 — 통화 창의 '○○ 녹화 중' 초기값 */
  initialRecorders?: CallRecorder[];
  /** 참가 시점의 화이트보드(먼저 그려 둔 내용) */
  initialWhiteboard?: { open: boolean; actions: any[] };
  maxParticipants?: number;
}

export interface IncomingCallInfo {
  channelId: number;
  channelName: string;
  callId: string;
  callerId: string;
  callerName: string;
  callerAvatar?: string;
  channelType?: string;
}

/** 대화방에서 진행 중인 통화('통화 중 · 참여' 바) — 서버 call:status */
export interface ChannelCallStatus {
  channelId: number;
  callId: string;
  active: boolean;
  startedAt?: string;
  participants: { userId: string; userName: string }[];
  /** 정원(서버 CALL_MAX_PARTICIPANTS) */
  max?: number;
}

// 소켓 이벤트 페이로드
export interface CallInitiatePayload {
  channelId: number;
  channelName: string;
  callId: string;
  callerId: string;
  callerName: string;
  callerAvatar?: string;
}

export interface CallParticipantPayload {
  channelId: number;
  callId: string;
  userId: string;
  userName: string;
  avatar?: string;
  seq?: number;
}

export interface RtcSignalPayload {
  callId: string;
  fromUserId: string;
  toUserId: string;
  signal: any;  // simple-peer SignalData
  pid?: string;
  toPid?: string;
}

// Window 객체 확장: Preload Script를 통해 노출된 electron API 정의
declare global {
  interface Window {
    electron?: {
      loginSuccess: (user: User) => void;
      logout: () => void;
      showNotification: (title: string, body: string, channelId: number) => void;
      onNavigateToChannel: (callback: (channelId: number) => void) => void;
      removeNavigateListener: () => void;
      getDefaultAffiliate: () => Promise<{ defaultAffiliate: string } | null>;
      windowMinimize: () => void;
      windowMaximize: () => void;
      windowClose: () => void;
      isMaximized: () => Promise<boolean>;
      // 자동 업데이트
      checkForUpdates: () => Promise<any>;
      downloadUpdate: () => Promise<any>;
      installUpdate: () => void;
      getAppVersion: () => Promise<string>;
      onUpdateChecking: (callback: () => void) => void;
      onUpdateAvailable: (callback: (info: { version: string; releaseDate?: string }) => void) => void;
      onUpdateNotAvailable: (callback: () => void) => void;
      onUpdateDownloadProgress: (callback: (progress: { percent: number; bytesPerSecond: number; transferred: number; total: number }) => void) => void;
      onUpdateDownloaded: (callback: (info: { version: string }) => void) => void;
      onUpdateError: (callback: (err: { message: string }) => void) => void;
      removeUpdateListeners: () => void;
    };
  }
}
